import { type StudyP4E2ERowSelectDto, StudyThreadRole, type StudyThreatTargetSelectDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { cn } from '@lib/utils';
import type { FC } from 'react';
import {
  type MeiaP4E2eColumnDef,
  type MeiaP4E2eFunctionCard,
  type MeiaP4E2eTargetCard,
  bucketP4E2eCards,
  meiaP4E2eColumnDefs,
} from './meia-p4-e2e.util';

type ThreadChipData = { id: string; name: string; role: StudyThreadRole };

const deriveThreadChips = (rows: StudyP4E2ERowSelectDto[]): ThreadChipData[] => {
  const seen = new Map<string, ThreadChipData>();
  for (const row of rows) {
    if (!seen.has(row.threadId)) {
      seen.set(row.threadId, { id: row.threadId, name: row.threadName, role: row.threadRole });
    }
  }
  return Array.from(seen.values());
};

type MeiaP4E2eViewProps = {
  isLoadingRows: boolean;
  isLoadingTargets: boolean;
  onSelectThread: (threadId: 'all' | string) => void;
  rows: StudyP4E2ERowSelectDto[];
  selectedThreadId: 'all' | string;
  threatTargets: StudyThreatTargetSelectDto[];
};

export const MeiaP4E2eView: FC<MeiaP4E2eViewProps> = (props) => {
  const { isLoadingRows, isLoadingTargets, onSelectThread, rows, selectedThreadId, threatTargets } = props;

  const threadChips = deriveThreadChips(rows);
  const cardsByColumn = bucketP4E2eCards({ rows, threatTargets });

  const totalSystems =
    cardsByColumn.sensors.length +
    cardsByColumn.c2.length +
    cardsByColumn.effectors.length +
    cardsByColumn.assess.length;
  const totalTargets = cardsByColumn.targets.length;

  return (
    <Tile
      contentClassName="gap-3"
      header={{ title: 'E2E Architecture' }}
    >
      <MeiaP4E2eToolbar
        onSelectThread={onSelectThread}
        selectedThreadId={selectedThreadId}
        threads={threadChips}
        totalSystems={totalSystems}
        totalTargets={totalTargets}
      />
      {!isLoadingRows && rows.length === 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          E2E View needs P3 task-technology assignments. Open P3 Tasks, edit a saved task, and attach technologies
          before expecting systems in these columns.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
        {meiaP4E2eColumnDefs.map((column) => (
          <MeiaP4E2eColumn
            cards={column.key === 'targets' ? cardsByColumn.targets : cardsByColumn[column.key]}
            column={column}
            isLoading={column.key === 'targets' ? isLoadingTargets : isLoadingRows}
            key={column.key}
            selectedThreadId={selectedThreadId}
          />
        ))}
      </div>
    </Tile>
  );
};

type ToolbarProps = {
  onSelectThread: (threadId: 'all' | string) => void;
  selectedThreadId: 'all' | string;
  threads: ReadonlyArray<{ id: string; name: string; role: StudyThreadRole }>;
  totalSystems: number;
  totalTargets: number;
};

const MeiaP4E2eToolbar: FC<ToolbarProps> = (props) => {
  const { onSelectThread, selectedThreadId, threads, totalSystems, totalTargets } = props;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap items-center gap-1">
        {threads.map((thread) => (
          <ThreadChip
            isSelected={selectedThreadId === thread.id}
            key={thread.id}
            onSelect={() => onSelectThread(selectedThreadId === thread.id ? 'all' : thread.id)}
            thread={thread}
          />
        ))}
        <button
          className={cn(
            'rounded border px-2 py-1 font-mono text-[11px] transition-colors',
            selectedThreadId === 'all'
              ? 'border-gray-300 bg-gray-100 text-gray-700'
              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400',
          )}
          onClick={() => onSelectThread('all')}
          type="button"
        >
          All
        </button>
      </div>
      <div className="ml-auto font-mono text-[10px] text-gray-400">
        {totalSystems} systems · {totalTargets} targets
      </div>
    </div>
  );
};

const threadRoleColor = (role: StudyThreadRole): string => {
  switch (role) {
    case StudyThreadRole.PRIMARY:
      return '#10b981';
    case StudyThreadRole.SHAPING:
      return '#f59e0b';
    case StudyThreadRole.SUPPORTING:
      return '#2563eb';
  }
};

type ThreadChipProps = {
  isSelected: boolean;
  onSelect: () => void;
  thread: { id: string; name: string; role: StudyThreadRole };
};

const ThreadChip: FC<ThreadChipProps> = (props) => {
  const { isSelected, onSelect, thread } = props;
  const color = threadRoleColor(thread.role);
  return (
    <button
      className={cn(
        'inline-flex items-center gap-1.5 rounded border px-2 py-1 font-mono text-[11px] transition-colors',
        isSelected ? 'border-transparent text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400',
      )}
      onClick={onSelect}
      style={isSelected ? { background: color } : undefined}
      type="button"
    >
      <span
        className="inline-block size-2 rounded-full"
        style={{ background: isSelected ? 'white' : color }}
      />
      <span>{thread.name}</span>
    </button>
  );
};

type MeiaP4E2eColumnProps = {
  cards: MeiaP4E2eFunctionCard[] | MeiaP4E2eTargetCard[];
  column: MeiaP4E2eColumnDef;
  isLoading: boolean;
  selectedThreadId: 'all' | string;
};

const MeiaP4E2eColumn: FC<MeiaP4E2eColumnProps> = (props) => {
  const { cards, column, isLoading, selectedThreadId } = props;
  const tintClass =
    column.key === 'sensors'
      ? 'bg-blue-50/40'
      : column.key === 'c2'
        ? 'bg-purple-50/40'
        : column.key === 'effectors'
          ? 'bg-red-50/40'
          : column.key === 'assess'
            ? 'bg-amber-50/40'
            : 'bg-rose-50/40';

  return (
    <div className={cn('flex min-h-[400px] flex-col gap-2 rounded-md p-3', tintClass)}>
      <div className="flex items-center gap-2 border-b border-dashed border-gray-200 pb-2">
        <span
          className="inline-block size-2.5 rounded-full"
          style={{ background: column.color }}
        />
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-700">
          {column.title}
        </span>
        <span className="ml-auto rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-600">
          {cards.length}
        </span>
      </div>
      {isLoading && cards.length === 0 ? (
        <div className="px-1 py-4 text-center text-[10px] italic text-gray-400">Loading…</div>
      ) : cards.length === 0 ? (
        <div className="px-1 py-4 text-center text-[10px] italic text-gray-400">— none —</div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {cards.map((card) => (
            <MeiaP4E2eCard
              card={card}
              isTarget={column.key === 'targets'}
              key={card.id}
              selectedThreadId={selectedThreadId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

type MeiaP4E2eCardProps =
  | { card: MeiaP4E2eFunctionCard; isTarget: false; selectedThreadId: 'all' | string }
  | { card: MeiaP4E2eTargetCard; isTarget: true; selectedThreadId: 'all' | string };

const MeiaP4E2eCard: FC<MeiaP4E2eCardProps> = (props) => {
  const isDimmed = props.selectedThreadId !== 'all' && !props.card.threadIds.includes(props.selectedThreadId);

  if (props.isTarget) {
    const { card } = props;
    return (
      <div
        className={cn(
          'rounded-md border border-red-200 bg-white px-2.5 py-2 text-center transition-opacity',
          isDimmed ? 'opacity-35' : 'opacity-100',
        )}
      >
        <div className="py-0.5 font-mono text-[11px] font-semibold text-red-900">{card.label}</div>
        <div className="font-mono text-[9px] text-red-700/80">
          {card.priority ? `P${card.priority} · ` : ''}
          {card.sub}
        </div>
      </div>
    );
  }
  const { card } = props;
  return (
    <div
      className={cn(
        'rounded-md border border-gray-200 bg-white px-2.5 py-2 transition-opacity',
        isDimmed ? 'opacity-35' : 'opacity-100',
      )}
    >
      <div className="flex items-start gap-2">
        <span className="flex-1 text-[12px] font-semibold leading-tight text-gray-900">{card.label}</span>
        <RoleBadge role={card.role} />
      </div>
      <div className="mt-0.5 font-mono text-[10px] leading-tight text-gray-500">{card.sub}</div>
    </div>
  );
};

type RoleBadgeProps = {
  role: MeiaP4E2eFunctionCard['role'];
};

const RoleBadge: FC<RoleBadgeProps> = (props) => {
  const { role } = props;
  const styles =
    role === StudyThreadRole.PRIMARY
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : role === StudyThreadRole.SUPPORTING
        ? 'border-blue-200 bg-blue-50 text-blue-700'
        : role === StudyThreadRole.SHAPING
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-slate-200 bg-slate-50 text-slate-600';
  return <span className={cn('shrink-0 rounded border px-1.5 py-0.5 font-mono text-[9px]', styles)}>{role}</span>;
};
